import type { RouteObject } from 'react-router';

import type { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { listingApiFetchVolop } from 'modules/listing/api/volop/listingApiFetchVolop';
import { isListingExpired } from 'modules/listing/helpers/isListingExpired';
import { ExpiredVolopPageLoadable } from 'modules/listing/pages/volops/expired/ExpiredVolopPageLoadable';
import { UnpublishedVolopPageLoadable } from 'modules/listing/pages/volops/unpublished/UnpublishedVolopPageLoadable';
import { ViewVolopPageLoadable } from 'modules/listing/pages/volops/view/ViewVolopPageLoadable';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import type { AppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { renderLoader } from 'routing/utils/renderLoader/renderLoader';
import { getUserEnvironment } from 'zustand-stores/userEnvironmentStore';

import { volopSegmentedRoutes } from './volop.routes';

/**
 * Volop routes
 *
 * These routes are separate in their own file to facilitate maintenance, and
 * they're used on `main.route-objects.tsx`
 */
export const volopRouteObjects = volopSegmentedRoutes.map((segmentedRoute) => ({
  path: segmentedRoute.fullPath,
  ...renderLoader({
    loader: async ({ params }: AppRouteLoaderArgs<typeof segmentedRoute>) => {
      const { volopIdDashSlug = '' } = params;

      const { user } = getUserEnvironment();

      try {
        const [volop] = await Promise.all([
          listingApiFetchVolop(volopIdDashSlug.split('-')[0]),
          // It's possible this loadable won't be used if the volop is unpublished
          // but in most cases optimistically loading it is best
          ViewVolopPageLoadable.load(),
        ]);

        switch (volop.type) {
          case 'VOLOP':
            return {
              type: isListingExpired(volop.expires) ? 'EXPIRED_VOLOP' : 'VOLOP',
              volop,
              handle: {
                hideFooterDivider: Boolean(!user),
                urlByLocale: volop.url,
              } satisfies LayoutHandleProps,
            } as const;
          case 'UNPUBLISHED_VOLOP':
            await UnpublishedVolopPageLoadable.load();
            return {
              type: volop.type,
              unpublishedVolop: volop,
            } as const;
        }
      } catch {
        return null;
      }
    },
    render: (data) => {
      switch (data?.type) {
        case undefined:
          return <NotFoundPageLoadable noLayout />;
        case 'VOLOP':
          return <ViewVolopPageLoadable volop={data.volop} />;
        case 'EXPIRED_VOLOP':
          return <ExpiredVolopPageLoadable volop={data.volop} />;
        case 'UNPUBLISHED_VOLOP':
          return (
            <UnpublishedVolopPageLoadable
              unpublishedVolop={data.unpublishedVolop}
            />
          );
      }
    },
  }),

  handle: {
    defaultSearchType: 'VOLOP',
    showDesktopSearch: true,
  } satisfies LayoutHandleProps,
})) satisfies RouteObject[];
